import React from "react";
import { useAppSelector } from "./hooks/hooks";
import { Navigate } from "react-router-dom";
import { RootState } from "./store/store";
import {
  Text,
  Container,
  Paper,
  Group,
  Button,
  Loader,
  Title,
  Stack,
  Alert,
} from "@mantine/core";
import { IconLock, IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import FeatureLockedSVG from "./assets/Svg/FeatureLockedSVG";

interface SubscriptionRouteProps {
  featureId: string;
  element: JSX.Element;
  title?: string;
  description?: string;
  upgradeUrl?: string;
  contactSupportUrl?: string;
  fallbackComponent?: JSX.Element;
}

/**
 * SubscriptionRoute - A component that conditionally renders content based on subscription access
 *
 * @param featureId - Unique identifier for the feature being protected
 * @param element - The component to render if the user has access
 * @param title - Title displayed on the locked feature page
 * @param description - Custom description for the locked feature page
 * @param upgradeUrl - URL for the upgrade plan button
 * @param contactSupportUrl - URL for the contact support button
 * @param fallbackComponent - Optional custom component to display when access is denied
 */
const SubscriptionRoute: React.FC<SubscriptionRouteProps> = ({
  featureId,
  element,
  title = "Please Upgrade Your Subscription",
  description = "This feature is not available with your current subscription plan.",
  upgradeUrl = "/newsubscriptionpage",
  contactSupportUrl = "/support",
  fallbackComponent,
}) => {
  const { subscriptionData, loading, error } = useAppSelector(
    (state: RootState) => ({
      subscriptionData: state.subscriptionData,
      loading: state.subscriptionData.loading,
      error: state.subscriptionData.error,
    })
  );

  const currentSubscription = subscriptionData?.snapshot?.[0];
  // const subscriptionFeatures = currentSubscription?.features || {};
  // const hasFeatureAccess = subscriptionFeatures[featureId] === true;
  const subscriptionFeatures = currentSubscription?.features || [];
  const isActive = currentSubscription?.status === "Active";
  const hasFeatureAccess = isActive && subscriptionFeatures.includes(featureId);

  // Handle loading state
  if (loading) {
    return (
      // <Container size="md" py="xl">
      //   {/* <Paper shadow="sm" p="xl" radius="md" withBorder> */}
      //   <Stack align="center" spacing="md">
      //     <Loader size="xl" variant="bars" />
      //     {/* <Text color="dimmed">Loading subscription details...</Text> */}
      //   </Stack>
      //   {/* </Paper> */}
      // </Container>
      <Container
        size="md"
        py="xl"
        sx={{
          height: "80vh", // or 100vh if full page
          display: "flex",
          justifyContent: "center", // horizontal center
          alignItems: "center", // vertical center
        }}
      >
        <Stack align="center" spacing="md">
          <Loader size="xl" variant="bars" />
        </Stack>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Subscription Error"
          color="red"
        >
          <Text>
            There was a problem loading your subscription information. Please
            try again later.
          </Text>
          <Group position="right" mt="md">
            <Button
              component="a"
              href={contactSupportUrl}
              size="sm"
              variant="outline"
            >
              Contact Support
            </Button>
          </Group>
        </Alert>
      </Container>
    );
  }

  // Handle missing or inactive subscription: Redirect to subscription page
  if (!currentSubscription || !isActive) {
    return <Navigate to="/subscriptionpage" replace />;
  }

  // Show feature locked view
  if (!hasFeatureAccess) {
    if (fallbackComponent) return fallbackComponent;

    return (
      <Container size="lg">
        <Paper shadow="xs" p={50} radius="md" withBorder>
          <Stack align="center" spacing="xl">
            <Title order={2} align="center" fw={700}>
              {title}
            </Title>

            <div style={{ padding: "20px" }}>
              <FeatureLockedSVG />
            </div>

            <Text size="lg" color="dimmed" align="center" maw={600}>
              {description}
            </Text>

            <Group spacing="md" mt="xl">
              <Button
                component="a"
                href={upgradeUrl}
                size="md"
                radius="md"
                px={40}
                style={{ backgroundColor: "#ee374d" }}
              >
                Upgrade Plan
              </Button>
              <Button
                component="a"
                href={contactSupportUrl}
                size="md"
                variant="outline"
                radius="md"
                color="red"
                px={40}
              >
                Contact Support
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // User has access, render the protected content
  return element;
};

export default SubscriptionRoute;
